<script setup>
import Panel from '../common/Panel.vue';
import { PhxAutocomplete, PhxIcon } from '@phx-styleguides/pia';
import { useI18n } from 'vue-i18n';
import { useRecentTerms } from '../../composables/recentTerms';
import { computed, onMounted, provide, ref, useSlots, watch } from 'vue';
import SearchPanelEmptyState from '../search/SearchPanelEmptyState.vue';
import SearchPanelResults from '../search/SearchPanelResults.vue';
import debounce from 'lodash-es/debounce';
import AlgoliaSearch, { algoliaSearch } from '../../search/algolia/AlgoliaSearch';
import { showPanel } from '@phoenix/helpers/panel-helper';
import { MIN_TERM_LENGTH, SEARCH_QUERY_SUGGESTION_DELAY } from '../../helpers/search-helper';
import { useSearchPanel } from '@phoenix/composables/components/search/searchPanel';
import { SearchPanel } from '@sg/src/View/Components/Organisms/SearchPanel';
import PhxsgButton from '@sg/src/View/Components/Atoms/Button/phxsg-button.vue';

const props = defineProps({
  allBoutiquesImage: {
    type: Object,
    default: () => ({}),
  },
  emptyStateHitsPerType: {
    type: Object,
    default: () => ({}),
  },
  maxRecentSearch: {
    type: Number,
    default: 5,
  },
  customFilters: {
    type: Object,
    default: () => ({}),
  },
});

const slots = useSlots();
const { t } = useI18n();
const { addRecentTerm } = useRecentTerms('mainNavigation', props.maxRecentSearch);
const {
  applyQuery,
  autocompleteRef,
  currentQuery,
  hasEmptyState,
  isQueryResultEmpty,
  onChange,
  searchInitialized,
  setCustomQuery,
  setQueryDebounced,
  setQueryFromUrl,
  stagedQuery,
  urlQueryTerm,
} = useSearchPanel();

provide('customFilters', props.customFilters);

const searchLabel = t('MainNavigation.search');
const closeLabel = t('Accessibility.button.close');
const panelId = 'search-panel';
const isSearchPanelOpen = ref(false);
const hasNavigation = ref(false);

const suggestions = ref([]);

const panelOptions = computed(() => ({
  overlayClass: 'panel-overlay',
  containerClass: 'panel-body',
  closeAll: true,
  updateTabsIndicator: true,
  focusOnOpen: 'search-panel-autocomplete-wrapper',
  onShow: () => {
    searchInitialized.value = true;
    isSearchPanelOpen.value = true;
  },
  onHidden: () => {
    isSearchPanelOpen.value = false;
  },
}));

const fetchSuggestions = debounce(async (term) => {
  if (!term || term.length < MIN_TERM_LENGTH) {
    suggestions.value = [];
    return;
  }
  const algoliaSuggestions = await algoliaSearch.fetchQuerySuggestions(term);
  suggestions.value = algoliaSuggestions ?? [];
}, SEARCH_QUERY_SUGGESTION_DELAY);

const onBlur = () => {
  if (currentQuery.value && currentQuery.value === stagedQuery.value) {
    addRecentTerm(currentQuery.value);
  }
};

const onQuery = (value) => {
  if (value === urlQueryTerm.value) {
    return;
  }

  if (AlgoliaSearch.isEnabled()) {
    fetchSuggestions(value);
  }
  setQueryDebounced(value);
};

const onTermClicked = (term) => {
  setCustomQuery(term);
};

const onEnter = (value) => {
  if (!value || value.length < MIN_TERM_LENGTH) {
    return;
  }

  applyQuery(value);
  addRecentTerm(value);
};

onMounted(async () => {
  hasNavigation.value = document.querySelector('.phxsg-navigation') !== null;
  const panel = document.getElementById(panelId);

  await setQueryFromUrl();

  if (panel) {
    window.Phoenix.panels[panelId] = new SearchPanel(panel, panelOptions.value);
    if (urlQueryTerm.value) {
      showPanel(panelId);
    }
  }
});

watch([isSearchPanelOpen, autocompleteRef], () => {
  if (isSearchPanelOpen.value && autocompleteRef.value) {
    document.querySelector('#search-panel-autocomplete')?.focus();
  }
});
</script>

<template>
  <panel
    :id="panelId"
    :classes="['search-panel']"
    :header-classes="['search-panel-header']"
    :header-bordered="false"
    :body-header-container="false"
    :init-panel="false"
    body-data-cy="search-panel-body"
    tight
    no-slide
    full
    has-scrollable-content
  >
    <template #header>
      <div class="container">
        <ul class="search-panel-header__list" role="presentation">
          <li class="search-panel-header__list-start" role="presentation">
            <div class="search-panel-header__label">
              {{ searchLabel }}
            </div>
          </li>
          <li class="search-panel-header__list-center" role="presentation">
            <PhxsgButton
              v-if="hasNavigation"
              class="phxsg-navigation__logo"
              icon-id="logo"
              icon-only
              label="logo"
              size="logo"
              href="/"
            />
          </li>
          <li class="search-panel-header__list-end" role="presentation">
            <button
              class="btn btn--bare btn--fade btn--xs btn--icon-only"
              type="button"
              data-panel-close="search-panel"
              :aria-label="closeLabel"
            >
              <span class="btn__icon btn__icon--start">
                <phx-icon icon="close" :title="closeLabel" />
              </span>
              <span class="sr-only">{{ closeLabel }}</span>
            </button>
          </li>
        </ul>
      </div>
    </template>
    <template #content>
      <div class="search-panel-form">
        <div class="container">
          <form>
            <div id="search-panel-autocomplete-wrapper">
              <phx-autocomplete
                v-if="searchInitialized"
                id="search-panel-autocomplete"
                ref="autocompleteRef"
                name="search-panel-autocomplete"
                :label="searchLabel"
                clear-button-icon="surrounded-cross"
                :clear-button-label="t('SearchPanel.clear')"
                :placeholder="t('MainNavigation.search')"
                :loading-text="t('SearchPanel.loading')"
                :max-rows="3"
                :teleport-target="{ '': '.search-panel-suggestions-placeholder' }"
                :options="suggestions"
                :close-on-input-blur="false"
                open-button-icon="magnifying-glass"
                no-result-text=""
                display-both-buttons
                :min-characters="3"
                hide-label
                :auto-focus-first-option="false"
                @autocomplete:query="onQuery"
                @autocomplete:change="onChange"
                @autocomplete:blur="onBlur"
                @autocomplete:press-enter="onEnter"
              >
              </phx-autocomplete>
            </div>
          </form>
        </div>
      </div>
      <div class="search-panel-results">
        <div class="container">
          <div class="search-panel-suggestions-placeholder"></div>
          <template v-if="searchInitialized">
            <div v-show="isQueryResultEmpty" class="search-no-result">
              <p>{{ $t(`Search.query.all.noResult`) }}</p>
            </div>
            <search-panel-empty-state
              v-show="hasEmptyState"
              :all-boutiques-image="allBoutiquesImage"
              :max-recent-search="maxRecentSearch"
              :hits-per-type="emptyStateHitsPerType"
              @term-clicked="onTermClicked"
            >
              <template v-if="slots.contacts" #contacts>
                <slot name="contacts" />
              </template>
            </search-panel-empty-state>
          </template>
          <search-panel-results v-if="!hasEmptyState">
            <template v-if="slots.contacts" #contacts>
              <slot name="contacts" />
            </template>
          </search-panel-results>
        </div>
      </div>
    </template>
  </panel>
</template>
